import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';

export default function AppShell({ title, actions, children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 1024) setSidebarOpen(false); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (sidebarOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen(o => !o), []);

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: 'var(--cream, #FAF7F2)',
    }}>

      {/* -- Sidebar -- */}
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />

      {/* -- Overlay (mobile) -- */}
      {sidebarOpen && (
        <div
          onClick={closeSidebar}
          style={{
            display: window.innerWidth < 1024 ? 'block' : 'none',
            position: 'fixed', inset: 0,
            background: 'rgba(10, 24, 56, 0.4)',
            zIndex: 999,
            backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* -- Main -- */}
      <div style={{
        flex: 1,
        marginLeft: 'var(--sidebar-width, 240px)',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
        className="main-content"
      >
        {/* -- Topbar -- */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 100,
          background: 'rgba(250, 247, 242, 0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--line, #E5E7EB)',
          padding: '0 24px',
          minHeight: 56,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          {/* Hamburger - mobile only */}
          <button
            onClick={toggleSidebar}
            className="menu-toggle"
            aria-label="Toggle menu"
            style={{
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              width: 40, height: 40,
              borderRadius: 8,
              border: '1px solid var(--line, #E5E7EB)',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--navy, #0A1838)',
              flexShrink: 0,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              {sidebarOpen
                ? <path d="M18 6L6 18M6 6l12 12" />
                : <path d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>

          {/* Page title */}
          <h1 style={{
            flex: 1, margin: 0,
            color: 'var(--navy, #0A1838)',
            fontFamily: 'var(--font-display, "DM Serif Display", serif)',
            fontSize: '1.4rem',
            fontWeight: 400,
            letterSpacing: '-0.015em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {title}
          </h1>

          {/* Actions */}
          {actions && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {actions}
            </div>
          )}
        </div>

        {/* -- Page content -- */}
        <div style={{
          flex: 1,
          padding: 'clamp(12px, 2vw, 24px)',
          overflow: 'auto',
          background: 'var(--cream, #FAF7F2)',
        }}>
          {children}
        </div>
      </div>
    </div>
  );
}
